import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  apiCatalogMenu,
  apiCatalogChildren,
  apiCatalogProducts,
} from "../../api/catalog.api";
import { trackEvent } from "../../services/amplitude";

export default function useCatalogSheetNavigation({ open, lang, t }) {
  const [loading, setLoading] = useState(false);

  // Перший рівень каталогу = таби
  const [topCategories, setTopCategories] = useState([]);
  const [activeTopCategoryId, setActiveTopCategoryId] = useState(null);

  // Контент під активним top-tab
  const [mode, setMode] = useState("categories");
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  // Глибша навігація тільки після top-tabs
  const [stack, setStack] = useState([]);

  const trackedRef = useRef(false);

  const activeTopCategory = useMemo(() => {
    return (
      topCategories.find((item) => String(item.id) === String(activeTopCategoryId)) ||
      null
    );
  }, [topCategories, activeTopCategoryId]);

  const title = useMemo(() => {
    if (stack.length) {
      return stack[stack.length - 1]?.title || t("title");
    }

    return t("title");
  }, [stack, t]);

  const resetState = useCallback(() => {
    setLoading(false);
    setTopCategories([]);
    setActiveTopCategoryId(null);
    setMode("categories");
    setCategories([]);
    setProducts([]);
    setStack([]);
  }, []);

  const loadProductsForCategory = useCallback(
    async (categoryId) => {
      const prods = await apiCatalogProducts(categoryId, lang);
      setMode("products");
      setProducts(prods || []);
      setCategories([]);
    },
    [lang]
  );

  const loadTopCategoryContent = useCallback(
    async (categoryId) => {
      const children = await apiCatalogChildren(categoryId, lang);

      setStack([]);

      if (children?.length) {
        setMode("categories");
        setCategories(children);
        setProducts([]);
      } else {
        await loadProductsForCategory(categoryId);
      }
    },
    [lang, loadProductsForCategory]
  );

  const selectTopCategory = useCallback(
    async (cat) => {
      const nextId = cat?.id ?? cat;

      if (!nextId) return;

      setLoading(true);
      try {
        setActiveTopCategoryId(String(nextId));
        await loadTopCategoryContent(nextId);
      } catch (e) {
        console.warn("selectTopCategory error:", e?.message);
      } finally {
        setLoading(false);
      }
    },
    [loadTopCategoryContent]
  );

  const openNestedCategory = useCallback(
    async (cat) => {
      if (!cat?.id) return;

      setLoading(true);
      try {
        const children = await apiCatalogChildren(cat.id, lang);

        setStack((prev) => [...prev, { id: String(cat.id), title: cat.title }]);

        if (children?.length) {
          setMode("categories");
          setCategories(children);
          setProducts([]);
        } else {
          await loadProductsForCategory(cat.id);
        }
      } catch (e) {
        console.warn("openNestedCategory error:", e?.message);
      } finally {
        setLoading(false);
      }
    },
    [lang, loadProductsForCategory]
  );

  const goBack = useCallback(async () => {
    if (!stack.length) return;

    const nextStack = stack.slice(0, -1);
    setStack(nextStack);

    setLoading(true);
    try {
      if (!nextStack.length) {
        if (activeTopCategoryId) {
          await loadTopCategoryContent(activeTopCategoryId);
        }
        return;
      }

      const parent = nextStack[nextStack.length - 1];
      const children = await apiCatalogChildren(parent.id, lang);

      if (children?.length) {
        setMode("categories");
        setCategories(children);
        setProducts([]);
      } else {
        await loadProductsForCategory(parent.id);
      }
    } catch (e) {
      console.warn("goBack error:", e?.message);
    } finally {
      setLoading(false);
    }
  }, [activeTopCategoryId, lang, loadProductsForCategory, loadTopCategoryContent, stack]);

  useEffect(() => {
    if (!open) return;

    if (!trackedRef.current) {
      trackedRef.current = true;
      trackEvent("catalog_opened", {
        source: "main_menu",
        lang,
      });
    }

    let alive = true;

    (async () => {
      setLoading(true);

      try {
        const top = await apiCatalogMenu(lang);
        if (!alive) return;

        const normalizedTop = top || [];

        setTopCategories(normalizedTop);
        setStack([]);

        if (normalizedTop.length) {
          const first = normalizedTop[0];
          setActiveTopCategoryId(String(first.id));
          await loadTopCategoryContent(first.id);
        } else {
          setActiveTopCategoryId(null);
          setMode("categories");
          setCategories([]);
          setProducts([]);
        }
      } catch (e) {
        console.warn("catalog menu error:", e?.message);
        if (!alive) return;

        resetState();
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, lang, loadTopCategoryContent, resetState]);

  return {
    loading,
    title,
    mode,
    categories,
    products,
    stack,
    topCategories,
    activeTopCategory,
    activeTopCategoryId,
    selectTopCategory,
    openNestedCategory,
    goBack,
  };
}